import { RoleName } from '@prisma/client';
import { NextResponse } from 'next/server';
import { ensureDatabaseHydrated } from './initDatabase';

export { RoleName };

export interface AuthUser {
  id: string;
  username: string;
  name: string;
  email: string;
  roles: RoleName[];
  permissions: string[];
}

export const PERMISSIONS = {
  // PR Permissions
  PR_READ: 'pr:read',
  PR_CREATE: 'pr:create',
  PR_EDIT: 'pr:edit',
  PR_SUBMIT: 'pr:submit',
  PR_APPROVE: 'pr:approve',
  PR_REJECT: 'pr:reject',

  // PO Permissions
  PO_READ: 'po:read',
  PO_CREATE: 'po:create',
  PO_EDIT: 'po:edit',
  PO_ISSUE: 'po:issue',
  PO_CANCEL: 'po:cancel',

  // GR Permissions
  GR_READ: 'gr:read',
  GR_CREATE: 'gr:create',
  GR_CONFIRM: 'gr:confirm',
  GR_CANCEL: 'gr:cancel',

  // Inventory Permissions
  INVENTORY_READ: 'inventory:read',
  INVENTORY_ADJUST: 'inventory:adjust',

  // Master Data Permissions
  MASTER_READ: 'master:read',
  MASTER_MANAGE: 'master:manage',

  // Finance & Accounts Payable Permissions
  FINANCE_READ: 'finance:read',
  FINANCE_REVIEW: 'finance:review',
  FINANCE_APPROVE: 'finance:approve',
  FINANCE_DISBURSE: 'finance:disburse',

  // Makloon & Production Permissions (Paket 2)
  MAKLOON_READ: 'makloon:read',
  MAKLOON_CREATE: 'makloon:create',
  MAKLOON_APPROVE: 'makloon:approve',
  MAKLOON_DISPATCH: 'makloon:dispatch',
  MAKLOON_RECEIVE: 'makloon:receive',

  // Admin & Audit
  USERS_MANAGE: 'users:manage',
  AUDIT_READ: 'audit:read',
} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Predefined permissions mapped to each role
 */
export const ROLE_PERMISSIONS: Record<RoleName, PermissionCode[]> = {
  ADMIN: Object.values(PERMISSIONS),
  OWNER: [
    PERMISSIONS.PR_READ,
    PERMISSIONS.PR_APPROVE,
    PERMISSIONS.PR_REJECT,
    PERMISSIONS.PO_READ,
    PERMISSIONS.GR_READ,
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.MASTER_READ,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.FINANCE_APPROVE,
    PERMISSIONS.MAKLOON_READ,
    PERMISSIONS.MAKLOON_APPROVE,
  ],
  PURCHASER: [
    PERMISSIONS.PR_READ,
    PERMISSIONS.PR_CREATE,
    PERMISSIONS.PR_EDIT,
    PERMISSIONS.PR_SUBMIT,
    PERMISSIONS.PO_READ,
    PERMISSIONS.PO_CREATE,
    PERMISSIONS.PO_EDIT,
    PERMISSIONS.PO_ISSUE,
    PERMISSIONS.PO_CANCEL,
    PERMISSIONS.GR_READ,
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.MASTER_READ,
    PERMISSIONS.MASTER_MANAGE,
    PERMISSIONS.MAKLOON_READ,
    PERMISSIONS.MAKLOON_CREATE,
  ],
  WAREHOUSE: [
    PERMISSIONS.PO_READ,
    PERMISSIONS.GR_READ,
    PERMISSIONS.GR_CREATE,
    PERMISSIONS.GR_CONFIRM,
    PERMISSIONS.INVENTORY_READ,
    PERMISSIONS.INVENTORY_ADJUST,
    PERMISSIONS.MASTER_READ,
    PERMISSIONS.MAKLOON_READ,
    PERMISSIONS.MAKLOON_DISPATCH,
    PERMISSIONS.MAKLOON_RECEIVE,
  ],
  FINANCE_ADMIN: [
    PERMISSIONS.PO_READ,
    PERMISSIONS.GR_READ,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.FINANCE_REVIEW,
    PERMISSIONS.FINANCE_DISBURSE,
    PERMISSIONS.MASTER_READ,
    PERMISSIONS.MAKLOON_READ,
  ],
  FINANCE_MANAGER: [
    PERMISSIONS.PO_READ,
    PERMISSIONS.GR_READ,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.FINANCE_APPROVE,
    PERMISSIONS.MASTER_READ,
    PERMISSIONS.AUDIT_READ,
    PERMISSIONS.MAKLOON_READ,
  ],
};

/**
 * Resolves aggregated unique permissions from an array of roles.
 */
export function getPermissionsForRoles(roles: RoleName[]): string[] {
  const perms = new Set<string>();
  for (const role of roles) {
    const list = ROLE_PERMISSIONS[role] || [];
    for (const p of list) {
      perms.add(p);
    }
  }
  return Array.from(perms);
}

/**
 * Check if a user possesses at least one of the required roles.
 * ADMIN role automatically bypasses and grants access.
 */
export function hasRole(
  userRoles: RoleName[] | null | undefined,
  allowedRoles: RoleName[]
): boolean {
  if (!userRoles || userRoles.length === 0) return false;
  if (userRoles.includes('ADMIN')) return true;
  return allowedRoles.some((role) => userRoles.includes(role));
}

/**
 * Check if user has a specific permission.
 * ADMIN role possesses all permissions.
 */
export function hasPermission(
  user: { roles: RoleName[]; permissions?: string[] } | null | undefined,
  permission: string
): boolean {
  if (!user) return false;
  if (user.roles.includes('ADMIN')) return true;
  const userPerms = user.permissions ?? getPermissionsForRoles(user.roles);
  return userPerms.includes(permission);
}

/**
 * Check if user has any of the requested permissions.
 */
export function hasAnyPermission(
  user: { roles: RoleName[]; permissions?: string[] } | null | undefined,
  permissions: string[]
): boolean {
  if (!user) return false;
  if (user.roles.includes('ADMIN')) return true;
  const userPerms = user.permissions ?? getPermissionsForRoles(user.roles);
  return permissions.some((perm) => userPerms.includes(perm));
}

/**
 * Mock demo user profiles representing different roles for UI switching & API requests
 */
export const DEMO_USERS: Record<string, AuthUser> = {
  ADMIN: {
    id: 'usr-admin-01',
    username: 'admin',
    name: 'System Administrator',
    email: 'admin@ptxyz.com',
    roles: ['ADMIN'],
    permissions: getPermissionsForRoles(['ADMIN']),
  },
  OWNER: {
    id: 'usr-owner-01',
    username: 'owner',
    name: 'Budi Santoso (Owner)',
    email: 'owner@ptxyz.com',
    roles: ['OWNER'],
    permissions: getPermissionsForRoles(['OWNER']),
  },
  PURCHASER: {
    id: 'usr-purchaser-01',
    username: 'purchaser',
    name: 'Siti Rahma (Purchaser)',
    email: 'purchaser@ptxyz.com',
    roles: ['PURCHASER'],
    permissions: getPermissionsForRoles(['PURCHASER']),
  },
  WAREHOUSE: {
    id: 'usr-warehouse-01',
    username: 'warehouse',
    name: 'Agus Wibowo (Warehouse Lead)',
    email: 'warehouse@ptxyz.com',
    roles: ['WAREHOUSE'],
    permissions: getPermissionsForRoles(['WAREHOUSE']),
  },
  MULTI_ROLE: {
    id: 'usr-multi-01',
    username: 'superops',
    name: 'Rian Perdana (Purchaser & Warehouse)',
    email: 'rian@ptxyz.com',
    roles: ['PURCHASER', 'WAREHOUSE'],
    permissions: getPermissionsForRoles(['PURCHASER', 'WAREHOUSE']),
  },
  FINANCE_ADMIN: {
    id: 'usr-fin-02',
    username: 'siti.aminah',
    name: 'Siti Aminah (Finance Admin - Tagihan)',
    email: 'siti.aminah@ptxyz.com',
    roles: ['FINANCE_ADMIN'],
    permissions: getPermissionsForRoles(['FINANCE_ADMIN']),
  },
  FINANCE_CASHIER: {
    id: 'usr-fin-01',
    username: 'dewi',
    name: 'Dewi Lestari (Finance Admin - Kasir)',
    email: 'dewi@ptxyz.com',
    roles: ['FINANCE_ADMIN'],
    permissions: getPermissionsForRoles(['FINANCE_ADMIN']),
  },
  FINANCE_MANAGER: {
    id: 'usr-fin-mgr-01',
    username: 'finmanager',
    name: 'Hendra Wijaya (Finance Manager)',
    email: 'hendra@ptxyz.com',
    roles: ['FINANCE_MANAGER'],
    permissions: getPermissionsForRoles(['FINANCE_MANAGER']),
  },
};

/**
 * Server-side API Authorization helper
 * Extracts user identity from headers (e.g., 'x-user-role' or 'x-user-id') or authorization bearer token.
 * Validates role and permission requirements.
 */
export async function authenticateApiRequest(req: Request): Promise<AuthUser | null> {
  let roleHeader = req.headers.get('x-user-role') as string | null;
  let userIdHeader = req.headers.get('x-user-id');

  // Fallback to cookies if headers missing
  if (!userIdHeader || !roleHeader) {
    const cookieHeader = req.headers.get('cookie') || '';
    const cookies = Object.fromEntries(
      cookieHeader.split(';').map((c) => {
        const [k, ...v] = c.trim().split('=');
        return [k, v.join('=')];
      })
    );
    if (!userIdHeader && cookies.x_user_id) userIdHeader = cookies.x_user_id;
    if (!roleHeader && cookies.x_user_role) roleHeader = cookies.x_user_role;
  }

  if (userIdHeader) {
    const matched = Object.values(DEMO_USERS).find((u) => u.id === userIdHeader);
    if (matched) return matched;
  }

  if (roleHeader && DEMO_USERS[roleHeader]) {
    return DEMO_USERS[roleHeader];
  }

  // Default fallback for dev/testing when no headers are supplied
  return DEMO_USERS.ADMIN;
}

/**
 * Guard function for Next.js API route handlers
 */
export async function authorizeApiRequest(
  req: Request,
  requirements?: {
    roles?: RoleName[];
    permissions?: string[];
  }
): Promise<{ user: AuthUser; errorResponse?: never } | { user?: never; errorResponse: NextResponse }> {
  await ensureDatabaseHydrated();
  const user = await authenticateApiRequest(req);

  if (!user) {
    return {
      errorResponse: NextResponse.json(
        { success: false, error: 'Unauthorized: Authentication required' },
        { status: 401 }
      ),
    };
  }

  if (requirements?.roles && requirements.roles.length > 0) {
    const allowed = hasRole(user.roles, requirements.roles);
    if (!allowed) {
      return {
        errorResponse: NextResponse.json(
          {
            success: false,
            error: `Forbidden: Requires one of the following roles: ${requirements.roles.join(', ')}`,
          },
          { status: 403 }
        ),
      };
    }
  }

  if (requirements?.permissions && requirements.permissions.length > 0) {
    const allowed = hasAnyPermission(user, requirements.permissions);
    if (!allowed) {
      return {
        errorResponse: NextResponse.json(
          {
            success: false,
            error: `Forbidden: Missing required permission`,
          },
          { status: 403 }
        ),
      };
    }
  }

  return { user };
}
